import {api} from './api';

export type MyStrategySourceType = 'FOLLOW' | 'ORIGINAL';

export interface MyStrategyItem {
    id: number;
    title: string;
    strategyName?: string;
    description?: string;
    category?: string;
    profit?: number;
    winRate?: number;
    maxDrawdown?: number;
    rating?: number;
    sharpeRatio?: number;
    chartData?: string;
    sourceMarketId?: number;
    sourceType: MyStrategySourceType;
    isActive?: number;
    strategyConfigId?: number;
    isRunning?: number;
    prompt?: string;
    strategyCode?: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface MyStrategyListResponse {
    list: MyStrategyItem[];
    total: number;
    page: number;
    size: number;
    totalPages: number;
}

export interface CreateMyStrategyRequest {
    title: string;
    strategyName?: string;
    version?: string;
    prompt?: string;
    strategyCode?: string;
    visualModel?: string;
    description?: string;
    isActive?: boolean;
}

export interface StrategyCodeValidateRequest {
    strategyCode: string;
    strategyName?: string;
}

export interface StrategyCodeValidateResponse {
    valid: boolean;
    errors: string[];
    warnings: string[];
    strategyClassName?: string;
}

export interface StrategyVisualMethod {
    name: string;
    body: string;
}

export interface StrategyVisualModelNode {
    id: string;
    type: string;
    label: string;
    payload?: Record<string, any> | null;
    x?: number;
    y?: number;
}

export interface StrategyVisualModelEdge {
    id: string;
    source: string;
    target: string;
}

export interface StrategyVisualModel {
    schemaVersion?: string;
    editorType?: string;
    source?: string;
    strategyName?: string;
    strategyClassName?: string;
    title?: string;
    description?: string;
    rawCode?: string;
    legacyParsed?: boolean;
    properties?: {
        timeframe?: string;
        canShort?: boolean;
        stoploss?: string;
        minimalRoi?: Record<string, number> | string;
    };
    methods?: {
        populateIndicators?: StrategyVisualMethod;
        entry?: StrategyVisualMethod;
        exit?: StrategyVisualMethod;
    };
    indicators?: Array<{
        name: string;
        expression: string;
    }>;
    nodes?: StrategyVisualModelNode[];
    edges?: StrategyVisualModelEdge[];

    [key: string]: any;
}

export interface StrategyCodeToVisualRequest {
    strategyCode: string;
    strategyName?: string;
    title?: string;
    description?: string;
    visualModel?: string;
}

export interface StrategyCodeToVisualResponse {
    visualModel: string;
    strategyClassName?: string;
    fromPersistedVisualModel: boolean;
    legacyParsed: boolean;
    warnings: string[];
}

export interface StrategyVisualToCodeRequest {
    visualModel: string;
    strategyName?: string;
    title?: string;
    description?: string;
}

export interface StrategyVisualToCodeResponse {
    strategyCode: string;
    strategyClassName?: string;
    warnings: string[];
}

export interface StrategyVisualValidateRequest {
    visualModel: string;
    strategyCode?: string;
    strategyName?: string;
    title?: string;
    description?: string;
}

export interface StrategyVisualValidateResponse {
    valid: boolean;
    errors: string[];
    warnings: string[];
    normalizedVisualModel?: string;
    generatedStrategyCode?: string;
    strategyClassName?: string;
}

class MyStrategyService {
    async getMyStrategies(page: number = 0, size: number = 12, filter: string = 'all'): Promise<MyStrategyListResponse> {
        const response = await api.get('/api/v1/strategies/my', {
            params: {page, size, filter}
        });
        if (!response.data?.success || response.data?.code !== '0') {
            throw new Error(response.data?.message || '获取我的策略列表失败');
        }
        return response.data.data;
    }

    async createMyStrategy(payload: CreateMyStrategyRequest): Promise<MyStrategyItem> {
        const response = await api.post('/api/v1/strategies/my', payload);
        if (!response.data?.success || response.data?.code !== '0' || !response.data?.data?.id) {
            throw new Error(response.data?.message || '创建策略失败');
        }
        return response.data.data;
    }

    async deleteMyStrategy(id: number): Promise<void> {
        const response = await api.delete(`/api/v1/strategies/my/${id}`);
        if (!response.data?.success || response.data?.code !== '0') {
            throw new Error(response.data?.message || '删除策略失败');
        }
    }

    async getMyStrategyById(id: number): Promise<MyStrategyItem> {
        const response = await api.get(`/api/v1/strategies/my/${id}`);
        if (!response.data?.success || response.data?.code !== '0') {
            throw new Error(response.data?.message || '获取策略详情失败');
        }
        return response.data.data;
    }

    async updateMyStrategy(id: number, payload: CreateMyStrategyRequest): Promise<MyStrategyItem> {
        const response = await api.put(`/api/v1/strategies/my/${id}`, payload);
        if (!response.data?.success || response.data?.code !== '0') {
            throw new Error(response.data?.message || '更新策略失败');
        }
        return response.data.data;
    }

    async validateStrategyCode(payload: StrategyCodeValidateRequest): Promise<StrategyCodeValidateResponse> {
        const response = await api.post('/api/v1/strategies/my/validate-code', payload);
        if (!response.data?.success || response.data?.code !== '0') {
            throw new Error(response.data?.message || '验证策略代码失败');
        }
        return response.data.data;
    }

    async getStrategyVisualModel(id: number): Promise<StrategyCodeToVisualResponse> {
        const response = await api.get(`/api/v1/strategies/my/${id}/visual-model`);
        if (!response.data?.success || response.data?.code !== '0') {
            throw new Error(response.data?.message || '获取可视化模型失败');
        }
        return response.data.data;
    }

    /** 将策略源码直接转换为 V2 逻辑流可视化模型 */
    async convertCodeToV2(strategyCode: string): Promise<string> {
        const response = await api.post('/api/v1/strategies/my/convert-to-v2', {strategyCode});
        if (!response.data?.success || response.data?.code !== '0') {
            throw new Error(response.data?.message || '转换 V2 模型失败');
        }
        return response.data.data.visualModel;
    }

    async convertStrategyCodeToVisual(payload: StrategyCodeToVisualRequest): Promise<StrategyCodeToVisualResponse> {
        const response = await api.post('/api/v1/strategies/my/convert-to-visual', payload);
        if (!response.data?.success || response.data?.code !== '0') {
            throw new Error(response.data?.message || '转换可视化模型失败');
        }
        return response.data.data;
    }

    async convertVisualToCode(payload: StrategyVisualToCodeRequest): Promise<StrategyVisualToCodeResponse> {
        const response = await api.post('/api/v1/strategies/my/convert-to-code', payload);
        if (!response.data?.success || response.data?.code !== '0') {
            throw new Error(response.data?.message || '转换策略代码失败');
        }
        return response.data.data;
    }

    async validateStrategyVisualModel(payload: StrategyVisualValidateRequest): Promise<StrategyVisualValidateResponse> {
        const response = await api.post('/api/v1/strategies/my/validate-visual', payload);
        if (!response.data?.success || response.data?.code !== '0') {
            throw new Error(response.data?.message || '校验可视化模型失败');
        }
        return response.data.data;
    }

    async saveVisualStrategy(payload: CreateMyStrategyRequest, id?: number): Promise<MyStrategyItem> {
        if (typeof id === 'number') {
            return this.updateMyStrategy(id, payload);
        }
        return this.createMyStrategy(payload);
    }
}

export const myStrategyService = new MyStrategyService();
